import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { LanguageSwitch } from './components/LanguageSwitch';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { TenantSwitcher } from './components/TenantSwitcher';
import { adminLogout, getProfile, getSelectedTenant, getToken, setSelectedTenant } from './api/client';
import { LoginPage } from './pages/LoginPage';
import { UsersPage } from './pages/UsersPage';
import { GroupsPage } from './pages/GroupsPage';
import { AssignmentsPage } from './pages/AssignmentsPage';
import { ContentLibraryPage } from './pages/ContentLibraryPage';
import { TranslationsPage } from './pages/TranslationsPage';
import { ContentTypesPage } from './pages/ContentTypesPage';
import { LtiPlatformsPage } from './pages/LtiPlatformsPage';
import { ReportsPage } from './pages/ReportsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { TenantsPage } from './pages/TenantsPage';
import { AiBuilderPage } from './pages/AiBuilderPage';

const BASE_NAV = [
  { to: '/content', key: 'nav.contentLibrary' },
  { to: '/assignments', key: 'nav.assignments' },
  { to: '/groups', key: 'nav.groups' },
  { to: '/users', key: 'nav.users' },
  { to: '/translations', key: 'nav.translations' },
  { to: '/content-types', key: 'nav.contentTypes' },
  { to: '/lti-platforms', key: 'nav.ltiPlatforms' },
  { to: '/reports', key: 'nav.reports' },
  { to: '/audit', key: 'nav.auditLog' },
] as const;

/**
 * Khung ứng dụng quản trị.
 *
 * Chưa đăng nhập thì chỉ render màn đăng nhập. Đây là hàng rào tiện lợi, không
 * phải hàng rào bảo mật — mọi API tự kiểm token, và DoD Phase 6 đòi kiểm bằng
 * curl chứ không qua UI.
 */
export function App(): ReactNode {
  const { t } = useTranslation(['common', 'admin']);
  const [signedIn, setSignedIn] = useState(() => Boolean(getToken()));
  const [changingPassword, setChangingPassword] = useState(false);
  const [tenantVersion, setTenantVersion] = useState(0);
  const [selectedTenant, setCurrentTenant] = useState<string | undefined>(() => getSelectedTenant());
  const profile = getProfile();
  const isSystemAdmin = Boolean(profile?.roles?.includes('system_admin'));

  if (!signedIn) return <LoginPage onSignedIn={() => setSignedIn(true)} />;

  const signOut = async (): Promise<void> => {
    await adminLogout();
    setSignedIn(false);
  };

  const handleTenantChange = (tenantId: string | undefined): void => {
    setSelectedTenant(tenantId);
    setCurrentTenant(tenantId);
    setTenantVersion((v) => v + 1);
  };

  const navItems = isSystemAdmin
    ? [
        { to: '/tenants', key: 'nav.tenants' as const },
        ...BASE_NAV,
        { to: '/ai-builder', key: 'nav.aiBuilder' as const },
      ]
    : BASE_NAV;

  return (
    <div className="app">
      <header className="app__header">
        <div className="app__header-brand">
          <h1 className="app__title">{t('app.adminTitle')}</h1>
        </div>

        <div className="app__header-center">
          <TenantSwitcher
            isSystemAdmin={isSystemAdmin}
            selectedTenant={selectedTenant}
            profile={profile}
            onTenantChange={handleTenantChange}
          />
        </div>

        <div className="app__header-actions">
          {profile ? (
            <span className="app__greeting" title={profile.principalId}>
              <span className="app__user-icon" aria-hidden="true">
                👤
              </span>
              <span>{profile.displayName}</span>
            </span>
          ) : null}
          <LanguageSwitch />
          <button type="button" onClick={() => setChangingPassword(true)}>
            {t('admin:password.action')}
          </button>
          <button type="button" onClick={() => void signOut()}>
            {t('admin:login.signOut')}
          </button>
        </div>
      </header>

      <div className="app__body">
        <nav className="app__nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'nav-link nav-link--active' : 'nav-link')}
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <main className="app__main" key={tenantVersion}>
          <Routes>
            <Route path="/" element={<Navigate to={isSystemAdmin ? '/tenants' : '/content'} replace />} />
            {isSystemAdmin ? (
              <Route
                path="/tenants"
                element={
                  <TenantsPage
                    onTenantSwitched={() => {
                      setCurrentTenant(getSelectedTenant());
                      setTenantVersion((v) => v + 1);
                    }}
                  />
                }
              />
            ) : null}
            <Route path="/content" element={<ContentLibraryPage />} />
            <Route path="/assignments" element={<AssignmentsPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/translations" element={<TranslationsPage />} />
            <Route path="/content-types" element={<ContentTypesPage />} />
            <Route path="/lti-platforms" element={<LtiPlatformsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            {isSystemAdmin ? <Route path="/ai-builder" element={<AiBuilderPage />} /> : null}
            <Route path="*" element={<Navigate to={isSystemAdmin ? '/tenants' : '/content'} replace />} />
          </Routes>
        </main>
      </div>

      {/* Đổi mật khẩu xong thì server đã thu hồi mọi phiên — đăng xuất ngay
          thay vì để token chết trong tab và người dùng gặp 401 ở thao tác sau. */}
      {changingPassword ? (
        <ChangePasswordDialog
          onClose={() => setChangingPassword(false)}
          onChanged={() => {
            setChangingPassword(false);
            void signOut();
          }}
        />
      ) : null}
    </div>
  );
}
